import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuChevronLeft, LuChevronRight, LuDownload, LuEye, LuEyeOff, LuImageOff, LuX } from 'react-icons/lu';
import { cx } from '../../utils/cx.js';
import { channelLabel } from '../../utils/sources.js';
import { useDialog } from '../../hooks/useDialog.js';
import Button from '../ui/Button';
import IconButton from '../ui/IconButton';
import { SpinnerRing } from '../ui/Spinner';

const SWIPE_THRESHOLD = 50;

const formatDate = (date) => (date ? date.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : null);

/**
 * Full-screen photo viewer. ←/→ (or swipe) browse the photos currently listed, Escape closes.
 * Admins hide/show a photo without leaving the viewer. Authors are only shown to admins.
 */
const GalleryLightbox = ({ pictures, index, onIndexChange, onClose, isAdminUser, isHidden, onToggleHidden, pendingId = null }) => {
  const panelRef = useRef(null);
  const touchStart = useRef(null);
  // Load state keyed by photo, so a cached image that fires `load` instantly is never reset.
  const [loaded, setLoaded] = useState({ id: null, status: 'loading' });
  const isOpen = index !== null && index >= 0 && index < pictures.length;
  useDialog({ isOpen, onClose, panelRef });

  const picture = isOpen ? pictures[index] : null;
  const hasPrev = isOpen && index > 0;
  const hasNext = isOpen && index < pictures.length - 1;

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.defaultPrevented) return;
      if (e.key === 'ArrowLeft' && hasPrev) { e.preventDefault(); onIndexChange(index - 1); }
      if (e.key === 'ArrowRight' && hasNext) { e.preventDefault(); onIndexChange(index + 1); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, index, hasPrev, hasNext, onIndexChange]);

  if (!isOpen) return null;

  const hidden = isHidden(picture.imgId);
  const status = loaded.id === picture.imgId ? loaded.status : 'loading';
  const setStatus = (value) => setLoaded({ id: picture.imgId, status: value });
  const neighbours = [pictures[index - 1], pictures[index + 1]].filter(Boolean);

  const onTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    touchStart.current = null;
    if (dx > SWIPE_THRESHOLD && hasPrev) onIndexChange(index - 1);
    if (dx < -SWIPE_THRESHOLD && hasNext) onIndexChange(index + 1);
  };

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${index + 1} sur ${pictures.length}`}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col bg-black text-white outline-none"
    >
      <div className="flex flex-none items-center gap-3 px-3 py-2.5 sm:px-5">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold tabular-nums">{index + 1} / {pictures.length}</p>
          <p className="truncate text-xs text-white/60">
            {picture.channel ? `#${channelLabel(picture.channel)}` : ''}
            {picture.date ? ` · ${formatDate(picture.date)}` : ''}
          </p>
        </div>
        {isAdminUser && (
          <Button
            size="sm"
            variant="secondary"
            icon={hidden ? LuEye : LuEyeOff}
            loading={pendingId === picture.imgId}
            onClick={() => onToggleHidden(picture.imgId)}
            className="!border-white/20 !bg-white/10 !text-white hover:!bg-white/20"
          >
            {hidden ? 'Afficher' : 'Masquer'}
          </Button>
        )}
        <Button
          as="a"
          size="sm"
          href={picture.blob_url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Télécharger la photo"
          title="Télécharger la photo"
          icon={LuDownload}
          className="!border-white/20 !bg-white/10 !text-white hover:!bg-white/20"
        >
          <span className="hidden sm:inline">Télécharger</span>
        </Button>
        <IconButton icon={LuX} label="Fermer la visionneuse" variant="overlay" size="md" onClick={onClose} />
      </div>

      <div
        className="relative flex min-h-0 flex-1 items-center justify-center px-2 sm:px-16"
        onTouchStart={(e) => { touchStart.current = e.touches[0].clientX; }}
        onTouchEnd={onTouchEnd}
      >
        {status === 'loading' && <SpinnerRing size="lg" label="Chargement de la photo" className="absolute" />}
        {status === 'error' ? (
          <div className="flex flex-col items-center gap-2 text-white/60">
            <LuImageOff className="h-10 w-10" aria-hidden />
            <p className="text-sm">Cette photo n'a pas pu être chargée.</p>
          </div>
        ) : (
          <img
            key={picture.imgId}
            src={picture.blob_url}
            alt={picture.text ? `Photo : ${picture.text.slice(0, 120)}` : `Photo ${index + 1}`}
            onLoad={() => setStatus('ready')}
            onError={() => setStatus('error')}
            className={cx(
              'max-h-full max-w-full select-none rounded-lg object-contain transition-opacity duration-200',
              status !== 'ready' ? 'opacity-0' : hidden ? 'opacity-60 grayscale' : 'opacity-100',
            )}
          />
        )}
        {hidden && status === 'ready' && (
          <span className="absolute left-1/2 top-3 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-red-600 px-3 py-1 text-xs font-semibold">
            <LuEyeOff className="h-3.5 w-3.5" aria-hidden /> Masquée pour le client
          </span>
        )}
        {hasPrev && (
          <IconButton icon={LuChevronLeft} label="Photo précédente" variant="overlay" size="lg" onClick={() => onIndexChange(index - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 sm:left-4" />
        )}
        {hasNext && (
          <IconButton icon={LuChevronRight} label="Photo suivante" variant="overlay" size="lg" onClick={() => onIndexChange(index + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 sm:right-4" />
        )}
        {neighbours.map((n) => <img key={n.imgId} src={n.blob_url} alt="" className="hidden" aria-hidden />)}
      </div>

      {(picture.text || (isAdminUser && picture.username)) && (
        <div className="flex-none px-3 pb-4 pt-2 sm:px-5">
          <div className="mx-auto max-h-32 max-w-3xl overflow-y-auto rounded-xl bg-white/10 px-4 py-3 text-sm leading-relaxed text-white/85 scrollbar-thin">
            {isAdminUser && picture.username && <p className="mb-1 text-xs font-semibold text-white">{picture.username}</p>}
            {picture.text && <p className="whitespace-pre-wrap">{picture.text}</p>}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default GalleryLightbox;
